import { z } from 'zod';
/**
 * Fan profile record — the per-fan row in `nx-fans-<env>` DDB table.
 * Created by the post-confirmation Lambda when a fan completes Cognito
 * email-verification (or Google federation). Lives independently of the
 * B2B operator profile (separate Cognito pool, separate DB).
 *
 * Per `fan-identity` capability: Cognito holds auth identity only;
 * displayName / locale / consent timestamps live here so handlers can
 * mutate without going through AdminUpdateUserAttributes.
 */
export const FanGenderSchema = z.enum(['male', 'female', 'other', 'prefer-not-to-say']);
/**
 * Per `fan-youtube-integration` — a fan's linked YouTube identity.
 * Populated by the OAuth callback; the SPA-visible fields here are
 * safe to return to the fan. The refresh token is stored at
 * `linkedYouTube.refreshTokenEncrypted` server-side, KMS-encrypted,
 * and stripped from any client-facing read.
 */
export const FanLinkedYouTubeSchema = z.object({
    /** YouTube channel id (UCxxxxxxxxxxxxxxxxxxxxxx, 24 chars). */
    channelId: z.string().regex(/^UC[A-Za-z0-9_-]{22}$/),
    /** Display name on YouTube (snippet.title). */
    channelTitle: z.string().max(120),
    /** snippet.thumbnails.default.url (88x88 typically). */
    thumbnailUrl: z.string().url(),
    /** When the link was first established. */
    linkedAt: z.string().datetime(),
    /**
     * KMS-encrypted Google OAuth refresh token. Base64-encoded
     * ciphertext from `kms:Encrypt`. NEVER returned to the client —
     * `getMyProfile` strips this before responding.
     */
    refreshTokenEncrypted: z.string().optional(),
});
/**
 * Subset of {@link FanLinkedYouTube} returned to the fan SPA — the
 * encrypted refresh token is stripped before responding.
 */
export const FanLinkedYouTubePublicSchema = FanLinkedYouTubeSchema.omit({
    refreshTokenEncrypted: true,
});
/**
 * Cognito-identity summary. Derived from `cognito:user.Identities`
 * + the user pool's UserStatus / EmailVerified attributes.
 * Returned as `linkedAuthProviders` on the profile response.
 */
export const FanLinkedAuthProviderSchema = z.object({
    provider: z.enum(['password', 'google']),
    linkedAt: z.string().datetime(),
});
export const FanProfileSchema = z.object({
    /** Cognito sub (UUID v4 from Fan User Pool). Primary key. */
    fanId: z.string().uuid(),
    /** Lowercased email. Cognito enforces uniqueness at the pool level. */
    email: z.string().email().toLowerCase(),
    /**
     * Fan-visible display name. Empty string acceptable for email-signup
     * fans who haven't edited their profile yet; populated from Google
     * `given_name + family_name` when SSO federation creates the row.
     */
    displayName: z.string().max(80),
    /** BCP-47 locale; default 'zh-TW' for unattributed signups. */
    locale: z.string().max(20).default('zh-TW'),
    /** YYYY-MM-DD. Optional — collected by the post-signup setup modal. */
    birthday: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '生日格式為 YYYY-MM-DD').optional(),
    /** Optional — collected by the post-signup setup modal. */
    gender: FanGenderSchema.optional(),
    /**
     * Set when the fan completes the post-signup profile setup modal.
     * Absent for legacy rows that predate the modal. Drives the modal
     * trigger: SPA shows the modal whenever this field is missing.
     */
    profileCompletedAt: z.string().datetime().optional(),
    /** Per `fan-youtube-integration` — populated by the YT OAuth flow. */
    linkedYouTube: FanLinkedYouTubeSchema.optional(),
    /**
     * Operator/admin-only private annotation, set from the 粉絲清單 page.
     * NEVER exposed to the fan; only callers with `fans.list-edit` can read
     * or write it. Free-text, capped to keep the row small.
     */
    adminNote: z.string().max(500).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Body for PATCH /api/admin/fan-list/{fanId} — admin-editable subset.
 *  `newPassword`(superadmin-only)直接重設粉絲登入密碼 — 客服處理
 *  「收不到驗證信」的粉絲用;規則鏡射 fan pool passwordPolicy
 *  (8+ 字元、大寫、小寫、數字)。 */
export const AdminFanPatchBodySchema = z.object({
    adminNote: z.string().max(500).optional(),
    newPassword: z.string().min(8, '至少 8 個字元').max(256)
        .regex(/[A-Z]/, '需含大寫字母')
        .regex(/[a-z]/, '需含小寫字母')
        .regex(/\d/, '需含數字')
        .optional(),
}).refine((b) => b.adminNote !== undefined || b.newPassword !== undefined, {
    message: 'adminNote 或 newPassword 至少要有一個',
});
/** Body for PUT /api/fan/me — only the fan-editable subset. */
export const FanProfileUpdateBodySchema = z.object({
    displayName: z.string().min(1, '請填寫暱稱').max(80),
    birthday: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '生日格式為 YYYY-MM-DD').optional(),
    gender: FanGenderSchema.optional(),
});
/**
 * Subset of FanProfile returned by GET /api/fan/me. Strips the
 * KMS-encrypted refresh token from `linkedYouTube` so the response
 * stays safe-by-construction; adds `linkedAuthProviders` derived from
 * Cognito identities.
 */
export const FanProfileResponseSchema = FanProfileSchema.omit({
    linkedYouTube: true,
}).extend({
    linkedYouTube: FanLinkedYouTubePublicSchema.optional(),
    linkedAuthProviders: z.array(FanLinkedAuthProviderSchema).default([]),
});
